import { type ReactNode } from 'react'
import { Navigate, Route, Routes, useParams, useSearchParams } from 'react-router-dom'
import { AppLayout } from './components/AppLayout'
import { ScrollToTop } from './components/ScrollToTop'
import { HomePage } from './pages/HomePage'
import { OrganizationApprovalsPage } from './pages/OrganizationApprovalsPage'
import { NewOrganizationPage } from './pages/NewOrganizationPage'
import { OrganizationDetailPage } from './pages/OrganizationDetailPage'
import { OrgMembersPage } from './pages/OrgMembersPage'
import { OrgSecurityPage } from './pages/OrgSecurityPage'
import { JoinOrgPage } from './pages/JoinOrgPage'
import { EventPlannerPage } from './pages/EventPlannerPage'
import { EventEditorPage } from './pages/EventEditorPage'
import { EventManagementPage } from './pages/EventManagementPage'
import { PublicEventPage } from './pages/PublicEventPage'
import { TicketPage } from './pages/TicketPage'
import { RegisterPage } from './pages/RegisterPage'
import { TicketVerificationPage } from './pages/TicketVerificationPage'
import { NotificationsPage } from './pages/NotificationsPage'
import { SettingsPage } from './pages/SettingsPage'
import { PresentationsArchivePage } from './pages/PresentationsArchivePage'
import { useCapabilities } from './auth/useCapabilities'

/**
 * Backward-compatibility redirect for the old `/event/:id?tk=...` magic-link format.
 * The backend now generates `/e/:id/ticket?tk=...`, but older emails may still carry
 * the legacy path. This component transparently forwards to the new URL.
 */
function EventMagicLinkRedirect() {
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const tk = searchParams.get('tk')
  return <Navigate to={`/e/${id}/ticket${tk ? `?tk=${tk}` : ''}`} replace />
}

/**
 * Route guard that redirects non-admin callers to /home.
 *
 * Uses the Drools-evaluated `isAdmin` flag from the capabilities endpoint rather
 * than reading realm roles directly from the JWT, so the decision always flows
 * through the authorization engine.
 */
function AdminGuard({ children }: { children: ReactNode }) {
  const { isAdmin, isLoading } = useCapabilities()
  if (isLoading) return null
  if (!isAdmin) return <Navigate to="/home" replace />
  return <>{children}</>
}

/**
 * Root router.
 * /home                   → current organization home page (org detail or create-org CTA)
 * /organizations/list     → admin approval / reject table (admin-only, linked from sidebar)
 * /organizations/new      → 3-step creation wizard
 * /organizations/:id/edit → edit wizard
 * /organizations/:id      → organization detail page
 * /notifications          → full notification inbox (bell's "see all" link)
 * /settings               → admin-only settings (translations refresh)
 * /event-planner          → event list dashboard
 * /event-planner/new      → create event
 * /event-planner/:id/edit → edit event
 * /event-planner/:id/presentations → presentations archive (all requested/uploaded/reviewed files)
 * /event-planner/:id/verify → kiosk QR ticket verification (fullscreen, outside AppLayout)
 * /e/:id                  → public event page (outside AppLayout, event-branded)
 */
function App() {
  return (
    <>
      <ScrollToTop />
      <Routes>
        {/* ── Admin shell ── */}
        <Route element={<AppLayout />}>
          <Route index element={<Navigate to="/home" replace />} />
          <Route path="/home" element={<HomePage />} />
          {/* /organizations redirects to /home — the home page is now the org context page */}
          <Route path="/organizations" element={<Navigate to="/home" replace />} />
          <Route
            path="/organizations/list"
            element={
              <AdminGuard>
                <OrganizationApprovalsPage />
              </AdminGuard>
            }
          />
          <Route
            path="/settings"
            element={
              <AdminGuard>
                <SettingsPage />
              </AdminGuard>
            }
          />
          <Route path="/organizations/new" element={<NewOrganizationPage />} />
          <Route path="/organizations/:id/edit" element={<NewOrganizationPage />} />
          <Route path="/organizations/:id" element={<OrganizationDetailPage />} />
          <Route path="/users" element={<OrgMembersPage />} />
          <Route path="/org/security" element={<OrgSecurityPage />} />
          <Route path="/join" element={<JoinOrgPage />} />
          <Route path="/activity" element={<HomePage />} />
          <Route path="/notifications" element={<NotificationsPage />} />
          <Route path="/event-planner" element={<EventPlannerPage />} />
          <Route path="/event-planner/new" element={<EventEditorPage />} />
          <Route path="/event-planner/:id" element={<EventManagementPage />} />
          <Route path="/event-planner/:id/edit" element={<EventEditorPage />} />
          <Route path="/event-planner/:id/presentations" element={<PresentationsArchivePage />} />
        </Route>

        {/* ── Kiosk ticket verification (no AppLayout, event-branded fullscreen) ── */}
        <Route path="/event-planner/:id/verify" element={<TicketVerificationPage />} />

        {/* ── Public event page (no AppLayout, event-branded) ── */}
        <Route path="/e/:id" element={<PublicEventPage />} />
        <Route path="/e/:id/ticket" element={<TicketPage />} />
        <Route path="/e/:id/register" element={<RegisterPage />} />

        {/* ── Legacy magic-link redirect: /event/:id?tk=... → /e/:id/ticket?tk=... ── */}
        <Route path="/event/:id" element={<EventMagicLinkRedirect />} />
      </Routes>
    </>
  )
}

export default App
